import React from "react";
import { QRCodeSVG } from "qrcode.react";
import car from "../../Assets/Logos/caricon.jpg";

// ✅ safeString: converts ANY value (object, array, null, undefined) to a safe renderable string
const safeString = (value, fieldName = "unknown") => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (value instanceof Date) return value.toLocaleString();
  if (Array.isArray(value)) {
    console.warn(
      `⚠️ [ParivahanRC] Array field "${fieldName}":`,
      JSON.stringify(value),
    );
    return (
      value
        .map((v) => safeString(v))
        .filter(Boolean)
        .join(", ") || ""
    );
  }
  if (typeof value === "object") {
    console.error(
      `❌ [ParivahanRC] Object field "${fieldName}":`,
      JSON.stringify(value),
    );
    if (value.name) return String(value.name);
    if (value.value) return String(value.value);
    if (value.label) return String(value.label);
    if (value.code) return String(value.code);
    if (value.text) return String(value.text);
    // ✅ CRITICAL FIX: Return "" so "{}" never renders on screen
    return "";
  }
  return String(value);
};

// ✅ Build a safe QR string — all fields coerced to string before concatenation
const buildQRValue = (data) => {
  return [
    safeString(data.rc_number, "rc_number"),
    safeString(data.vehicle_chasi_number, "vehicle_chasi_number"),
    safeString(data.vehicle_engine_number, "vehicle_engine_number"),
    safeString(data.maker_description, "maker_description"),
    safeString(data.maker_model, "maker_model"),
    safeString(data.body_type, "body_type"),
    safeString(data.fuel_type, "fuel_type"),
    safeString(data.norms_type, "norms_type"),
    safeString(data.manufacturing_date, "manufacturing_date"),
    safeString(data.seat_capacity, "seat_capacity"),
    safeString(data.unladen_weight, "unladen_weight"),
    safeString(data.vehicle_gross_weight, "vehicle_gross_weight"),
    safeString(data.present_address, "present_address"),
    safeString(data.registration_date, "registration_date"),
    safeString(data.color, "color"),
    safeString(data.gvw, "gvw"),
    safeString(data.seat_capacity, "seat_capacity"),
    safeString(data.standing_capacity, "standing_capacity"),
    safeString(data.financer, "financer"),
    safeString(data.insurance_company, "insurance_company"),
    safeString(data.insurance_policy_number, "insurance_policy_number"),
    safeString(data.insurance_upto, "insurance_upto"),
    safeString(data.fit_up_to, "fit_up_to"),
    safeString(data.pucc_number, "pucc_number"),
    safeString(data.pucc_upto, "pucc_upto"),
    safeString(data.rto_code, "rto_code"),
  ].join("");
};

export default function VehicleRegistrationCertificate({ data }) {
  if (!data) return null;

  // ✅ Log all incoming data fields for debugging
  console.group(
    `🔍 [ParivahanRC] Data field type audit for VRN: ${safeString(data?.rc_number, "rc_number")}`,
  );
  Object.entries(data).forEach(([key, val]) => {
    const type = Array.isArray(val) ? "array" : typeof val;
    const isProblematic = type === "object" && val !== null;
    console.log(
      `  ${isProblematic ? "❌" : "✅"} ${key}: [${type}] = ${isProblematic ? JSON.stringify(val) : val}`,
    );
  });
  console.groupEnd();

  const qrValue = buildQRValue(data);
  const screenWidth = typeof window !== "undefined" ? window.innerWidth : 640;

  return (
    <div className="max-w-md mx-auto p-2 sm:p-4 bg-white rounded-lg shadow border border-gray-200 text-xs sm:text-sm text-gray-800 font-sans tracking-tighter">
      <div className="flex flex-col items-center mb-2">
        <div className="relative inline-block">
          <div className="p-4 sm:p-6">
            <QRCodeSVG
              value={qrValue || " "}
              level="H"
              size={screenWidth < 640 ? 80 : 112}
              includeMargin={false}
            />
          </div>
          {/* Blue corner markers */}
          <div className="absolute top-0 left-0 w-3 sm:w-4 h-3 sm:h-4 border-t-4 border-l-4 border-[#77BEF0]" />
          <div className="absolute top-0 right-0 w-3 sm:w-4 h-3 sm:h-4 border-t-4 border-r-4 border-[#77BEF0]" />
          <div className="absolute bottom-0 left-0 w-3 sm:w-4 h-3 sm:h-4 border-b-4 border-l-4 border-[#77BEF0]" />
          <div className="absolute bottom-0 right-0 w-3 sm:w-4 h-3 sm:h-4 border-b-4 border-r-4 border-[#77BEF0]" />
          {/* Center image icon */}
          <div className="absolute inset-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 sm:w-10 h-8 sm:h-10 bg-white rounded-full flex items-center justify-center border border-gray-200">
            <img
              src={car}
              alt="Car Icon"
              className="w-8 sm:w-10 h-8 sm:h-10 object-contain border-2 border-gray-900 rounded-full"
            />
          </div>
          <p className="mt-2 font-semibold text-xs sm:text-sm absolute left-[25%] -bottom-2">
            {safeString(data.rc_number, "rc_number")}
          </p>
        </div>
      </div>

      <br />

      <div className="my-2 sm:my-4 pb-2 sm:pb-4">
        <h1 className="text-gray-700 text-lg sm:text-xl font-bold border-t border-gray-300 pb-1 mb-2">
          Owner Details
        </h1>
        <div className="grid grid-cols-2 gap-y-1 text-xs sm:text-sm">
          <p className="text-gray-500 font-bold">Name</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.owner_name, "owner_name")}
          </p>
          <p className="text-gray-500 font-bold">Son/Daughter/Wife of</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.father_name, "father_name")}
          </p>
          <p className="text-gray-500 font-bold">Ownership</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.ownership, "ownership") || "INDIVIDUAL"}
          </p>
        </div>
      </div>

      <div className="border-t border-gray-300 py-2">
        <div className="grid grid-cols-2 gap-y-1 sm:gap-y-2 text-xs sm:text-sm">
          <p className="text-gray-500 font-bold">Chassis No.</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.vehicle_chasi_number, "vehicle_chasi_number")}
          </p>
          <p className="text-gray-500 font-bold">Engine No.</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.vehicle_engine_number, "vehicle_engine_number")}
          </p>
          <p className="text-gray-500 font-bold">Maker Name</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.maker_description, "maker_description")}
          </p>
          <p className="text-gray-500 font-bold">Model Name</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.maker_model, "maker_model")}
          </p>
          <p className="text-gray-500 font-bold">Registration Date</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.registration_date, "registration_date")}
          </p>
          <p className="text-gray-500 font-bold">Tax Valid UpTo</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.tax_upto, "tax_upto")}
          </p>
          <p className="text-gray-500 font-bold">Vehicle Class</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.vehicle_category, "vehicle_category")}
          </p>
          <p className="text-gray-500 font-bold">Description</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(
              data.vehicle_category_description,
              "vehicle_category_description",
            )}
          </p>
          <p className="text-gray-500 font-bold">Fuel Type</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.fuel_type, "fuel_type")}
          </p>
          <p className="text-gray-500 font-bold">Emission Norm</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.norms_type, "norms_type")}
          </p>
          <p className="text-gray-500 font-bold">Color</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.color, "color")}
          </p>
          <p className="text-gray-500 font-bold">GVW</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.vehicle_gross_weight, "vehicle_gross_weight")}
          </p>
          <p className="text-gray-500 font-bold">Standing Capacity</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.standing_capacity, "standing_capacity")}
          </p>
          <p className="text-gray-500 font-bold">Financier</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.financer, "financer")}
          </p>
          <p className="text-gray-500 font-bold">Insurance Company</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.insurance_company, "insurance_company")}
          </p>
          <p className="text-gray-500 font-bold">Policy No.</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(
              data.insurance_policy_number,
              "insurance_policy_number",
            )}
          </p>
          <p className="text-gray-500 font-bold">Insurance Valid Upto</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.insurance_upto, "insurance_upto")}
          </p>
          <p className="text-gray-500 font-bold">Fitness Valid Upto</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.fit_up_to, "fit_up_to")}
          </p>
          <p className="text-gray-500 font-bold">PUCC No.</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.pucc_number, "pucc_number")}
          </p>
          <p className="text-gray-500 font-bold">PUCC Valid Upto</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.pucc_upto, "pucc_upto")}
          </p>
          <p className="text-gray-500 font-bold">Registering Authority</p>
          <p className="text-[#77BEF0] font-bold text-right break-words">
            {safeString(data.registered_at, "registered_at")}
          </p>
        </div>
      </div>

      <div className="py-2 sm:py-4 text-xs sm:text-sm font-bold">
        <p className="text-[#77BEF0]">
          Tap to Check the Vehicle impound and Seizure Document Status
        </p>
        <br />
        <p>
          If status of pollution Certificate, Insurance, Tax etc, are not
          available above, same may be verified from physical documents
        </p>
        <br />
        <p>
          Note: This information for the Certificate of Registration is
          generated by mParivahan as per data provided by the issuing authority
          in the National Registry of Ministry of Road Transport and Highways.
          This document is valid as per the IT Act 2000 when used
          electronically.
        </p>
      </div>
    </div>
  );
}
